import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { proxiedImage, onImgError } from '../utils/images';

interface ListSummary {
  token: string;
  title: string;
  description: string;
  cover_image: string;
  saves: number;
  item_count: number;
  created_at: string;
}

const ListsPage: React.FC = () => {
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = (query = '') => {
    setLoading(true);
    fetch(`/api/lists${query ? `?q=${encodeURIComponent(query)}` : ''}`)
      .then((r) => r.json())
      .then((d) => { setLists(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen bg-cream-100">
      <SEO
        title="Shopping lists"
        description="Curated shopping lists from the GoodCart community: clean picks for every routine, searchable and shareable."
        image="/og-image.png"
        url="https://goodcart.org/lists"
      />

      <section className="bg-sage-700 text-cream-50">
        <div className="container-custom py-6 md:py-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow text-sage-200 mb-2">Community</p>
              <h1 className="text-2xl md:text-3xl font-display text-cream-50">Shopping lists</h1>
              <p className="mt-2 text-sm text-sage-100/90">Curated bundles from shoppers who read the labels so you don't have to.</p>
            </div>
            <Link to="/lists/new" className="btn bg-cream-50 text-sage-800 hover:bg-white">Create a list</Link>
          </div>
        </div>
      </section>

      <div className="container-custom py-8">
        <div className="max-w-md mb-8">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search lists..."
            aria-label="Search lists"
            className="w-full py-2.5 px-4 bg-white border border-earth-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sage-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : lists.length === 0 ? (
          <div className="bg-white rounded-2xl border border-earth-100 p-10 md:p-16 text-center max-w-xl mx-auto">
            <h2 className="font-display text-xl text-earth-900 mb-2">No lists yet</h2>
            <p className="text-earth-600 mb-8">Be the first to bundle your favorite clean picks into a shareable list.</p>
            <Link to="/lists/new" className="btn btn-primary">Create the first list</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {lists.map((l, i) => (
              <motion.div
                key={l.token}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.3) }}
              >
                <Link to={`/lists/${l.token}`} className="organic-card block overflow-hidden group h-full">
                  <div className="relative h-40 bg-gradient-to-br from-sage-200 to-cream-200 overflow-hidden">
                    {l.cover_image && (
                      <img src={proxiedImage(l.cover_image)} alt="" loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={onImgError()} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-earth-900/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <h2 className="font-display text-xl text-cream-50 drop-shadow leading-snug">{l.title}</h2>
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between text-sm text-earth-600">
                    <span className="tabular-nums">{l.item_count} items</span>
                    <span className="inline-flex items-center gap-1 text-sage-700">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>
                      {l.saves} {l.saves === 1 ? 'save' : 'saves'}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListsPage;
